import type React from "react";
import { useState } from "react";
import {
  Box,
  Paper,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  TextField,
  Menu,
  MenuItem,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Language as LanguageIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  People as PeopleIcon,
  Attachment as AttachmentIcon,
  MoreHoriz as MoreHorizIcon,
  DeleteOutline,
} from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import { useNavigate } from "react-router-dom";
import PenPaperIcon from "../../components/icons/PenPaperIcon";

// ─────────────────────────────────────────────
// Theme
// ─────────────────────────────────────────────
const companyTheme = createTheme({
  palette: {
    primary: { main: "#8B4513" },
    secondary: { main: "#c9302c" },
  },
  typography: { fontFamily: "inherit" },
});

// ─────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────
export const COMPANY_TABS = [
  "Overview",
  "Representatives",
  "Branches",
  "Bookings",
  "Notes",
] as const;

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
// REPLACE the CompanyDetails interface:
export interface CompanyDetails {
  id: number;
  name: string;
  company_type?: string;
  description?: string;
  company_logo?: string;
  phone_number?: string;
  primary_email?: string;
  secondary_email?: string;
  website?: string;
  street_address?: string;
  billing_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  license_number?: string;
  specialties?: string;
  is_active: boolean;
  tax_vat_number?: string;
  legal_name?: string;
  industry?: string;
  // correct social field names from API
  linkedin_profile?: string;
  instagram_profile?: string;
  facebook_profile?: string;
  youtube_profile?: string;
  // tags
  company_tags?: string[];
  latitude?: string;
  longitude?: string;
  time_zone?: string;
}

export interface Representative {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  role?: string;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
  branch_name?: string;
}

export interface Branch {
  id: number;
  name: string;
  city?: string;
  country?: string;
  contacts_count?: number;
  bookings_count?: number;
  manager?: {
    name?: string;
    avatar?: string;
  };
  initials?: string;
  color?: string;
}

export interface CompanyNote {
  id: number;
  author_name?: string;
  author_avatar?: string;
  created_at: string;
  content: string;
}

export interface LinkedContact {
  id: number;
  first_name?: string;
  last_name?: string;
  name?: string;
  avatar?: string | null;
}

export interface CompanyDocument {
  id: number;
  name: string;
  size?: string;
  date?: string;
  url?: string;
}

export interface CompanyBooking {
  id: number;
  status: string;
  booking_type: string;
  branch: string;
  package_name?: string | null;
  type_of_service?: string | null;
  date_of_service?: string | null;
  title?: string | null;
}

const INDUSTRY_LABELS: Record<string, string> = {
  streaming_company: "Streaming Company",
  funeral_home: "Funeral Home",
  insurance: "Insurance",
  hospital: "Hospital",
  clinic: "Clinic",
  pharmacy: "Pharmacy",
  law_firm: "Law Firm",
  accounting_firm: "Accounting Firm",
  real_estate: "Real Estate",
  logistics: "Logistics & Transport",
  retail: "Retail",
  hospitality: "Hospitality & Tourism",
  construction: "Construction",
  education: "Education",
  ngo: "NGO / Non-Profit",
  government: "Government",
  other: "Other",
};
export interface ViewCompanyDashboardProps {
  companyData: CompanyDetails;
  coverColor?: string; // fallback brand color for avatar bg
  healthScore: number;
  linkedContacts?: LinkedContact[];
  linkedContactsTotal?: number;
  documents?: CompanyDocument[];
  quickActions?: { label: string; onClick: () => void }[];
  representatives?: Representative[];
  bookings?: CompanyBooking[];
  branches?: Branch[];
  notes?: CompanyNote[];
  bookingsContent?: React.ReactNode;
  onEditClick: () => void;
  onAddRepresentative?: () => void;
  onAddBranch?: () => void;
  onAddDocument?: () => void;
  onAddNote?: (content: string) => Promise<void>;
  onBranchClick?: (branch: Branch) => void;
  formatDate: (date: string) => string;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}
function stringToColor(str: string) {
  const colors = [
    "#E11D48",
    "#7C3AED",
    "#2563EB",
    "#059669",
    "#D97706",
    "#DB2777",
    "#0891B2",
    "#65A30D",
  ];
  const idx = str.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return colors[idx % colors.length];
}

const BRANCH_COLORS = [
  "#7C3AED",
  "#2563EB",
  "#059669",
  "#D97706",
  "#DC2626",
  "#0891B2",
];

function branchColor(id: number) {
  return BRANCH_COLORS[id % BRANCH_COLORS.length];
}

function IconInfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "text.secondary",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.disabled", display: "block", mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {value || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

function SocialCard({
  icon,
  label,
  url,
}: {
  icon: React.ReactNode;
  label: string;
  url: string;
}) {
  return (
    <Paper
      variant="outlined"
      component="a"
      href={url.startsWith("http") ? url : `https://${url}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        textDecoration: "none",
        cursor: "pointer",
        transition: "background 0.15s",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={500}
          sx={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 150,
            color: "primary.main",
          }}
        >
          {url}
        </Typography>
      </Box>
    </Paper>
  );
}

function RepCard({ rep }: { rep: Representative }) {
  const navigate = useNavigate();
  const displayName =
    rep.first_name && rep.last_name
      ? `${rep.first_name} ${rep.last_name}`
      : rep.name || "Unknown";

  return (
    <Paper
      variant="outlined"
      onClick={() => navigate(`/contacts/individual/view/${rep.id}`)}
      sx={{
        p: 2,
        borderRadius: 2,
        textAlign: "center",
        cursor: "pointer",
        bgcolor: "background.paper",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Avatar
        src={rep.avatar || undefined}
        sx={{
          width: 56,
          height: 56,
          fontSize: 18,
          fontWeight: 700,
          mx: "auto",
          mb: 1,
          bgcolor: rep.avatar ? undefined : stringToColor(displayName),
        }}
      >
        {getInitials(displayName)}
      </Avatar>
      <Typography
        variant="body2"
        fontWeight={600}
        display="block"
        sx={{ fontSize: "0.85rem" }}
      >
        {displayName}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ fontSize: "0.75rem" }}
      >
        {rep.role || rep.branch_name || "—"}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5, mt: 1 }}>
        {rep.phone && (
          <IconButton
            size="small"
            component="a"
            href={`https://wa.me/${rep.phone.replace(/\D/g, "")}`}
            target="_blank"
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "#25D366",
              bgcolor: "rgba(37,211,102,0.08)",
              width: 30,
              height: 30,
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
        {rep.email && (
          <IconButton
            size="small"
            component="a"
            href={`mailto:${rep.email}`}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "#1565C0",
              bgcolor: "rgba(21,101,192,0.08)",
              width: 30,
              height: 30,
            }}
          >
            <EmailIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
        {rep.phone && (
          <IconButton
            size="small"
            component="a"
            href={`tel:${rep.phone}`}
            onClick={(e) => e.stopPropagation()}
            sx={{
              color: "#2E7D32",
              bgcolor: "rgba(46,125,50,0.08)",
              width: 30,
              height: 30,
            }}
          >
            <PhoneIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    </Paper>
  );
}
function BranchCard({
  branch,
  onClick,
}: {
  branch: Branch;
  onClick?: () => void;
}) {
  const color = branch.color || branchColor(branch.id);
  const initials = branch.initials || getInitials(branch.name);
  const location = [branch.city, branch.country].filter(Boolean).join(", ");

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: "12px",
        cursor: "pointer",
        bgcolor: "transparent",
        transition: "background 0.15s, box-shadow 0.15s",
        "&:hover": { bgcolor: "action.hover", boxShadow: 2 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar sx={{ bgcolor: color, width: 32, height: 32, fontSize: 12 }}>
            {initials}
          </Avatar>
          <Box>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ lineHeight: 1.2 }}
            >
              {branch.name}
            </Typography>
            {location && (
              <Typography variant="caption" color="text.secondary">
                {location}
              </Typography>
            )}
          </Box>
        </Box>
        <ChevronRightIcon
          sx={{ fontSize: 16, color: "text.disabled", mt: 0.5 }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 1,
          mt: 1.5,
        }}
      >
        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1.5,
            px: 1,
            py: 0.75,
            height: 44,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
          >
            Contacts
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {branch.contacts_count ?? 0}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1.5,
            px: 1,
            py: 0.75,
            height: 44,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
          >
            Bookings
          </Typography>
          <Typography variant="body2" fontWeight={700}>
            {branch.bookings_count ?? 0}
          </Typography>
        </Box>

        <Box
          sx={{
            bgcolor: "action.hover",
            borderRadius: 1.5,
            px: 1,
            py: 0.75,
            height: 44,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            overflow: "hidden",
          }}
        >
          {branch.manager?.name ? (
            <>
              <Avatar
                src={branch.manager.avatar}
                sx={{ width: 20, height: 20, fontSize: 8, flexShrink: 0 }}
              >
                {getInitials(branch.manager.name)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: "0.6rem", lineHeight: 1.1 }}
                >
                  Manager
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    fontSize: "0.68rem",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {branch.manager.name}
                </Typography>
              </Box>
            </>
          ) : (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontSize: "0.65rem" }}
              >
                Manager
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                —
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function NotePost({
  note,
  formatDate,
}: {
  note: CompanyNote;
  formatDate: (d: string) => string;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const initials = getInitials(note.author_name || "U");

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.75,
        borderRadius: 2,
        display: "flex",
        gap: 1.5,
      }}
    >
      <Avatar
        src={note.author_avatar}
        sx={{
          width: 32,
          height: 32,
          fontSize: 12,
          flexShrink: 0,
          bgcolor: note.author_avatar ? undefined : "primary.main",
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {note.author_name || "Unknown"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(note.created_at)}
          </Typography>
          <IconButton
            size="small"
            sx={{ ml: "auto" }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <MoreHorizIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem dense onClick={() => setAnchorEl(null)}>
              Edit
            </MenuItem>
            <MenuItem
              dense
              onClick={() => setAnchorEl(null)}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </Menu>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          {note.content}
        </Typography>
      </Box>
    </Paper>
  );
}
// ─────────────────────────────────────────────
// Tab Content Builders
// ─────────────────────────────────────────────

function OverviewTab({
  company,
  onEditClick,
  onAddSocial,
}: {
  company: CompanyDetails;
  onEditClick: () => void;
  onAddSocial?: () => void;
}) {
  const fullAddress = [
    company.street_address,
    company.billing_address,
    company.city,
    company.state,
    company.postal_code,
    company.country,
  ]
    .filter(Boolean)
    .join(", ");

  // REPLACE socials array in OverviewTab:
  const socials = [
    company.linkedin_profile && {
      icon: <LinkedInIcon sx={{ fontSize: 18 }} />,
      label: "LinkedIn",
      url: company.linkedin_profile,
    },
    company.youtube_profile && {
      icon: <YouTubeIcon sx={{ fontSize: 18 }} />,
      label: "YouTube",
      url: company.youtube_profile,
    },
    company.website && {
      icon: <LanguageIcon sx={{ fontSize: 18 }} />,
      label: "Website",
      url: company.website,
    },
    company.instagram_profile && {
      icon: <InstagramIcon sx={{ fontSize: 18 }} />,
      label: "Instagram",
      url: company.instagram_profile,
    },
    company.facebook_profile && {
      icon: <FacebookIcon sx={{ fontSize: 18 }} />,
      label: "Facebook",
      url: company.facebook_profile,
    },
  ].filter(Boolean) as { icon: React.ReactNode; label: string; url: string }[];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Company Contact */}
      {/* Company Contact */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            Company Contact
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PenPaperIcon sx={{ fontSize: 14 }} />}
            onClick={onEditClick}
            sx={{
              textTransform: "none",
              fontSize: 12,
              borderRadius: "999px",
              px: 2,
            }}
          >
            Edit
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            rowGap: 3,
            columnGap: 4,
          }}
        >
          <IconInfoRow
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label="Legal Name"
            value={company.legal_name || company.name}
          />
          <IconInfoRow
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label="Industry"
            value={
              INDUSTRY_LABELS[company.company_type ?? ""] ||
              company.company_type
            }
          />
          <IconInfoRow
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label="Tax/Vat No."
            value={company.tax_vat_number}
          />
          <Box />
          <IconInfoRow
            icon={<EmailIcon sx={{ fontSize: 18 }} />}
            label="Main Email"
            value={company.primary_email}
          />
          <IconInfoRow
            icon={<PhoneIcon sx={{ fontSize: 18 }} />}
            label="Main Phone"
            value={company.phone_number}
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <IconInfoRow
              icon={<LocationOnIcon sx={{ fontSize: 18 }} />}
              label="Registered Address"
              value={fullAddress}
            />
          </Box>
        </Box>
      </Paper>
      {/* Tags */}
      {company.company_tags && company.company_tags.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Tags
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {company.company_tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" sx={{ fontSize: 11 }} />
            ))}
          </Box>
        </Paper>
      )}

      {/* About */}
      {company.description && (
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 3, bgcolor: "action.hover" }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
            About
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            {company.description}
          </Typography>
        </Paper>
      )}

      {/* Social Profile */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Social Profile
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={onAddSocial}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            Add
          </Button>
        </Box>
        {socials.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1,
            }}
          >
            {socials.map((s) => (
              <SocialCard
                key={s.label}
                icon={s.icon}
                label={s.label}
                url={s.url}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.disabled">
            No social profiles added yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}

function RepresentativesTab({
  reps,
  companyName,
  onAdd,
}: {
  reps: Representative[];
  companyName: string;
  onAdd?: () => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Representatives at {companyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {reps.length} {reps.length === 1 ? "person" : "people"} · sorted by
            role
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={onAdd}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: 2 }}
        >
          Add Representative
        </Button>
      </Box>
      {reps.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 1.5,
          }}
        >
          {reps.map((rep) => (
            <RepCard key={rep.id} rep={rep} />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled">
          No representatives added yet.
        </Typography>
      )}
    </Paper>
  );
}

function BranchesTab({
  branches,
  companyName,
  onAdd,
  onBranchClick,
}: {
  branches: Branch[];
  companyName: string;
  onAdd?: () => void;
  onBranchClick?: (b: Branch) => void;
}) {
  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={700}>
            Branches of {companyName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {branches.length} {branches.length === 1 ? "branch" : "branches"} ·
            sorted by role
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={onAdd}
          sx={{ textTransform: "none", fontSize: 12, borderRadius: 2 }}
        >
          Add Branch
        </Button>
      </Box>
      {branches.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.5,
          }}
        >
          {branches.map((b) => (
            <BranchCard
              key={b.id}
              branch={b}
              onClick={() => onBranchClick?.(b)}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled">
          No branches added yet.
        </Typography>
      )}
    </Paper>
  );
}

function NotesTab({
  notes,
  companyName,
  onAddNote,
  formatDate,
}: {
  notes: CompanyNote[];
  companyName: string;
  onAddNote?: (content: string) => Promise<void>;
  formatDate: (d: string) => string;
}) {
  const [noteText, setNoteText] = useState("");
  const [saving, setSaving] = useState(false);

  const handlePost = async () => {
    if (!noteText.trim() || !onAddNote) return;
    setSaving(true);
    try {
      await onAddNote(noteText.trim());
      setNoteText("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 2, bgcolor: "background.paper" }}
    >
      {/* Compose */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder={`Add a note about ${companyName}`}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          variant="standard"
          InputProps={{ disableUnderline: true, sx: { fontSize: "0.85rem" } }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mt: 1,
            gap: 1,
          }}
        >
          <Button
            size="small"
            startIcon={<AttachmentIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: "none",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            Attach
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!noteText.trim() || saving}
            onClick={handlePost}
            sx={{
              textTransform: "none",
              fontSize: 12,
              borderRadius: 2,
              bgcolor: "#8B4513",
              "&:hover": { bgcolor: "#6d340f" },
            }}
          >
            {saving ? "Posting…" : "Post Note"}
          </Button>
        </Box>
      </Paper>

      {/* Feed */}
      {notes.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {notes.map((note) => (
            <NotePost key={note.id} note={note} formatDate={formatDate} />
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.disabled">
          No notes yet. Add the first one above.
        </Typography>
      )}
    </Paper>
  );
}

function BookingsTab({ bookings }: { bookings: CompanyBooking[] }) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  if (!bookings.length) {
    return (
      <Typography variant="caption" color="text.disabled">
        No bookings yet.
      </Typography>
    );
  }

  const statusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "confirmed":
        return { bgcolor: "#dcfce7", color: "#166534" };
      case "enquiry":
        return { bgcolor: "#fef9c3", color: "#854d0e" };
      case "cancelled":
        return { bgcolor: "#fee2e2", color: "#991b1b" };
      case "completed":
        return { bgcolor: "#e0f2fe", color: "#075985" };
      default:
        return { bgcolor: "#f3f4f6", color: "#374151" };
    }
  };

  const formatBookingDate = (date?: string | null) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const paginatedBookings = bookings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              {["Name", "Service", "Package", "Branch", "Date", "Status"].map(
                (col) => (
                  <th
                    key={col}
                    style={{
                      backgroundColor: reduxTheme.primary_colors?.[400],
                      color: "white",
                      padding: "10px 14px",
                      textAlign: "left",
                      fontWeight: 500,
                      fontSize: 13,
                      letterSpacing: 0.2,
                    }}
                  >
                    {col}
                  </th>
                ),
              )}
            </tr>
          </thead>

          <tbody>
            {paginatedBookings.map((booking, index) => (
              <tr
                key={booking.id}
                style={{
                  backgroundColor:
                    index % 2 === 0
                      ? reduxTheme.primary_colors?.[900]
                      : reduxTheme.primary_colors?.[800],
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => {
                  (
                    e.currentTarget as HTMLTableRowElement
                  ).style.backgroundColor = reduxTheme.primary_colors?.[800];
                }}
                onMouseLeave={(e) => {
                  (
                    e.currentTarget as HTMLTableRowElement
                  ).style.backgroundColor =
                    index % 2 === 0
                      ? reduxTheme.primary_colors?.[900]
                      : reduxTheme.primary_colors?.[800];
                }}
              >
                {/* Name */}
                <td style={{ padding: "8px 14px" }}>
                  <Typography variant="body2" fontSize={13} fontWeight={600}>
                    {booking.title || `Booking #${booking.id}`}
                  </Typography>
                </td>

                {/* Service */}
                <td style={{ padding: "8px 14px" }}>
                  <Typography variant="body2" fontSize={13}>
                    {booking.type_of_service ?? "—"}
                  </Typography>
                </td>

                {/* Package */}
                <td style={{ padding: "8px 14px" }}>
                  <Typography variant="body2" fontSize={13}>
                    {booking.package_name ?? "—"}
                  </Typography>
                </td>

                {/* Branch */}
                <td style={{ padding: "8px 14px" }}>
                  <Typography variant="body2" fontSize={13}>
                    {booking.branch ?? "—"}
                  </Typography>
                </td>

                {/* Date */}
                <td style={{ padding: "8px 14px" }}>
                  <Typography variant="body2" fontSize={13}>
                    {formatBookingDate(booking.date_of_service)}
                  </Typography>
                </td>

                {/* Status */}
                <td style={{ padding: "8px 14px" }}>
                  <Chip
                    label={booking.status}
                    size="small"
                    sx={{
                      fontSize: 10,
                      height: 18,
                      fontWeight: 700,
                      textTransform: "capitalize",
                      "& .MuiChip-label": { px: 1 },
                      ...statusColor(booking.status),
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      <TablePagination
        component="div"
        count={bookings.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[5]}
        onRowsPerPageChange={() => {}}
      />
    </Box>
  );
}
// ─────────────────────────────────────────────
// Main Dashboard
// ─────────────────────────────────────────────
const ViewCompanyDashboard: React.FC<ViewCompanyDashboardProps> = ({
  companyData,
  coverColor = "#2E7D32",
  healthScore,
  linkedContacts = [],
  linkedContactsTotal = 0,
  documents = [],
  quickActions = [],
  representatives = [],
  branches = [],
  notes = [],
  bookings = [],
  onEditClick,
  onAddRepresentative,
  onAddBranch,
  onAddDocument,
  onAddNote,
  onBranchClick,
  formatDate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const navigate = useNavigate();
  const fullAddress = [
    companyData.street_address,
    companyData.city,
    companyData.state,
    companyData.country,
  ]
    .filter(Boolean)
    .join(", ");

  const initials = getInitials(companyData.name);

  const repCount = representatives.length;

  // Social chip links
  const socialChips = [
    companyData.linkedin_profile && {
      icon: <LinkedInIcon sx={{ fontSize: 16 }} />,
      href: companyData.linkedin_profile,
      label: "LinkedIn",
    },
    companyData.youtube_profile && {
      icon: <YouTubeIcon sx={{ fontSize: 18 }} />,
      label: "YouTube",
      url: companyData.youtube_profile,
    },
    companyData.website && {
      icon: <LanguageIcon sx={{ fontSize: 16 }} />,
      href: companyData.website,
      label: "Website",
    },
    companyData.instagram_profile && {
      icon: <InstagramIcon sx={{ fontSize: 16 }} />,
      href: companyData.instagram_profile,
      label: "Instagram",
    },
    companyData.facebook_profile && {
      icon: <FacebookIcon sx={{ fontSize: 16 }} />,
      href: companyData.facebook_profile,
      label: "Facebook",
    },
    companyData.primary_email && {
      icon: <EmailIcon sx={{ fontSize: 16 }} />,
      href: `mailto:${companyData.primary_email}`,
      label: "Email",
    },
    companyData.phone_number && {
      icon: <WhatsAppIcon sx={{ fontSize: 16 }} />,
      href: `https://wa.me/${companyData.phone_number.replace(/\D/g, "")}`,
      label: "WhatsApp",
    },
  ].filter(Boolean) as { icon: React.ReactNode; href: string; label: string }[];
  const tabContent: Record<number, React.ReactNode> = {
    0: <OverviewTab company={companyData} onEditClick={onEditClick} />,
    1: (
      <RepresentativesTab
        reps={representatives}
        companyName={companyData.name}
        onAdd={onAddRepresentative}
      />
    ),
    2: (
      <BranchesTab
        branches={branches}
        companyName={companyData.name}
        onAdd={onAddBranch}
        onBranchClick={onBranchClick}
      />
    ),
    3: <BookingsTab bookings={bookings} />,
    4: (
      <NotesTab
        notes={notes}
        companyName={companyData.name}
        onAddNote={onAddNote}
        formatDate={formatDate}
      />
    ),
  };

  return (
    <ThemeProvider theme={companyTheme}>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
          {/* ── Row 1: Hero Header + Quick Actions (equal height) ── */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
            {/* Hero Header */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                }}
              >
                {/* Company Avatar */}
                <Avatar
                  src={companyData.company_logo ?? undefined}
                  sx={{
                    width: 100,
                    height: 100,
                    fontSize: 26,
                    fontWeight: 700,
                    flexShrink: 0,
                    bgcolor: companyData.company_logo ? undefined : coverColor,
                  }}
                >
                  {initials}
                </Avatar>

                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2, mb: 0.75 }}
                  >
                    {companyData.name}
                  </Typography>

                  {/* Metadata row */}
                  {/* Metadata row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2.5,
                      flexWrap: "wrap",
                      mb: 1.25,
                    }}
                  >
                    {(companyData.industry || companyData.company_type) && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <BusinessIcon
                          sx={{ fontSize: 15, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {companyData.industry || companyData.company_type}
                        </Typography>
                      </Box>
                    )}
                    {fullAddress && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <LocationOnIcon
                          sx={{ fontSize: 15, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {fullAddress}
                        </Typography>
                      </Box>
                    )}
                    {repCount > 0 && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <PeopleIcon
                          sx={{ fontSize: 15, color: "text.secondary" }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {repCount} Representatives
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Social chips */}
                  {/* Social chips */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {socialChips.map((chip) => (
                      <Box
                        key={chip.label}
                        component="a"
                        href={chip.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.6,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "999px",
                          border: "1px solid",
                          borderColor: "divider",
                          textDecoration: "none",
                          color: "text.secondary",
                          fontSize: 12,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {chip.icon}
                        {chip.label}
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Top right actions */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    position: "absolute",
                    top: 16,
                    right: 16,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={onEditClick}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      color: "text.secondary",
                    }}
                  >
                    <PenPaperIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      color: "error.main",
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {/* Health score ring */}
                <Box
                  sx={{
                    position: "relative",

                    width: 50,
                    height: 50,
                    flexShrink: 0,
                    mr: "auto",
                  }}
                >
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={48}
                    thickness={3.5}
                    sx={{ color: "action.hover", position: "absolute" }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={healthScore}
                    size={48}
                    thickness={3.5}
                    sx={{
                      color: healthScore >= 75 ? "#4CAF50" : "#2196F3",
                      position: "absolute",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                      {healthScore}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Quick Actions */}
            <Box sx={{ width: 220, flexShrink: 0 }}>
              {/* Quick Actions */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1.5 }}
                >
                  Quick Actions
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  {(quickActions.length > 0
                    ? quickActions.map((action) => ({
                        ...action,
                        icon: <AddIcon sx={{ fontSize: 14 }} />,
                      }))
                    : [
                        {
                          label: "Add Representative",
                          onClick: onAddRepresentative,
                          icon: <PeopleIcon sx={{ fontSize: 14 }} />,
                        },
                        {
                          label: "Add Booking",
                          onClick: undefined,
                          icon: <AddIcon sx={{ fontSize: 14 }} />,
                        },
                        {
                          label: "Upload Document",
                          onClick: onAddDocument,
                          icon: <AttachmentIcon sx={{ fontSize: 14 }} />,
                        },
                        {
                          label: "Add Note",
                          onClick: undefined,
                          icon: <AddIcon sx={{ fontSize: 14 }} />,
                        },
                      ]
                  ).map((action) => (
                    <Box
                      key={action.label}
                      onClick={action.onClick}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1,
                        py: 0.75,
                        borderRadius: 1.5,
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: "50%",
                            bgcolor: "action.hover",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          {action.icon}
                        </Box>
                        <Typography variant="caption" fontWeight={500}>
                          {action.label}
                        </Typography>
                      </Box>
                      <ChevronRightIcon
                        sx={{ fontSize: 14, color: "text.disabled" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* ── Row 2: Tabs+Content + (Linked Contacts / Documents / Notes) ── */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "stretch" }}>
            {/* Tabs + Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 2,
                  bgcolor: "background.paper",
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      textTransform: "none",
                      fontSize: 13,
                      px: 2,
                    },
                  }}
                >
                  {COMPANY_TABS.map((label) => (
                    <Tab key={label} label={label} />
                  ))}
                </Tabs>

                <Box sx={{ mt: 2 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor:
                        reduxTheme.primary_colors?.[800] || "background.paper",
                      minHeight: 200,
                    }}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                      >
                        {tabContent[activeTab]}
                      </motion.div>
                    </AnimatePresence>
                  </Paper>
                </Box>
              </Paper>
            </Box>

            {/* Linked Contacts / Documents / Notes */}
            <Box
              sx={{
                width: 220,
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {/* Linked Contacts */}
              {/* Linked Contacts */}
              <Paper
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, textAlign: "center" }}
              >
                {linkedContacts.length > 0 ? (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        rowGap: 1.5,
                        columnGap: 2,
                        justifyItems: "center",
                        mb: 2,
                        maxHeight: 150,
                        overflowY: "auto",
                      }}
                    >
                      {linkedContacts.map((contact, idx) => {
                        const name =
                          contact.first_name && contact.last_name
                            ? `${contact.first_name} ${contact.last_name}`
                            : contact.name || "?";
                        const isPrimary = idx === 0;
                        const initials = getInitials(name);

                        return (
                          <Box
                            key={contact.id}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Avatar
                              src={contact.avatar || undefined}
                              title={name}
                              onClick={() =>
                                navigate(
                                  `/contacts/individual/view/${contact.id}`,
                                )
                              }
                              sx={{
                                width: 48,
                                height: 48,
                                fontSize: 15,
                                fontWeight: 700,
                                cursor: "pointer",
                                bgcolor: contact.avatar
                                  ? undefined
                                  : stringToColor(name),
                              }}
                            >
                              {initials}
                            </Avatar>
                            {isPrimary && (
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: "error.main",
                                  lineHeight: 1,
                                }}
                              >
                                Primary
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>

                    <Typography variant="subtitle2" fontWeight={700}>
                      Linked Contacts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {linkedContactsTotal || linkedContacts.length} people
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ mb: 1 }}
                    >
                      Linked Contacts
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      No linked contacts.
                    </Typography>
                  </>
                )}
              </Paper>

              {/* Documents */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Documents
                  </Typography>
                  <IconButton size="small" onClick={onAddDocument}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {documents.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                  >
                    {documents.map((doc) => (
                      <Box
                        key={doc.id}
                        component="a"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderRadius: 1.5,
                          textDecoration: "none",
                          color: "inherit",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: 1,
                            bgcolor: "#fee2e2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 8,
                              fontWeight: 700,
                              color: "#dc2626",
                            }}
                          >
                            PDF
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            fontWeight={500}
                            sx={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {doc.name}
                          </Typography>
                          {(doc.size || doc.date) && (
                            <Typography
                              variant="caption"
                              color="text.disabled"
                              sx={{ fontSize: "0.62rem" }}
                            >
                              {[doc.size, doc.date].filter(Boolean).join(" · ")}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small" sx={{ flexShrink: 0 }}>
                          <AttachmentIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.disabled">
                    No documents uploaded.
                  </Typography>
                )}
              </Paper>

              {/* Notes (sidebar) */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Notes
                  </Typography>
                  <IconButton size="small" onClick={() => setActiveTab(4)}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                {notes.length > 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    {notes.length} note{notes.length !== 1 ? "s" : ""} added.{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="primary.main"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setActiveTab(4)}
                    >
                      View all
                    </Typography>
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      minHeight: 60,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      p: 1,
                    }}
                  />
                )}
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ViewCompanyDashboard;
